import { z } from 'zod';
import { asId, type Brand } from '@shared/brand';
import { CalendarDate } from '@domain/value-objects/CalendarDate';
import { DateRange } from '@domain/value-objects/DateRange';
import { UNKNOWN } from '@domain/vocabulary';

/**
 * The decoding boundary — and the mechanism that makes this client OTA-safe.
 *
 * Every document read from Firestore passes through a schema built from these
 * primitives. The rules they enforce, and why each one matters when a newer
 * backend and an older client are live at the same time:
 *
 *  1. **Every optional field has a fallback.** A field added by a newer build is
 *     absent in older documents; a decode that failed on absence would blank a
 *     whole screen for data that is merely older.
 *
 *  2. **Unknown enum values degrade to `Desconocido` rather than throwing.** A
 *     status this build has never heard of renders neutrally. The alternative is
 *     an exhaustiveness error in the middle of a list, which takes out the page.
 *
 *  3. **One bad document does not fail a page.** `decodeMany` skips and reports
 *     rather than rejecting the batch, so a single malformed record cannot hide
 *     the other 499.
 *
 * A field a newer deploy wrote and this build does not know about is dropped on
 * decode, because a domain entity has a fixed shape and there is nowhere to keep
 * it. That is safe here only because **no write is ever a whole-document round
 * trip**: every mutation is a field-level patch issued by a callable, so an older
 * client can never erase a newer client's field by reading and writing back. Any
 * future code that persists a decoded entity wholesale would break this property.
 */

/**
 * A schema whose *input* is `unknown`.
 *
 * Every primitive here is a transform over `z.unknown()`, because a Firestore
 * document is untrusted shapeless data. `z.ZodType<T>` defaults its input
 * parameter to `T`, which would wrongly assert the input is already decoded.
 */
export type Decoder<T> = z.ZodType<T, z.ZodTypeDef, unknown>;

/** A Firestore `Timestamp`, structurally — avoids importing the SDK here. */
interface TimestampLike {
  toDate(): Date;
}

const isTimestampLike = (value: unknown): value is TimestampLike =>
  typeof value === 'object' &&
  value !== null &&
  'toDate' in value &&
  typeof (value as TimestampLike).toDate === 'function';

/**
 * A moment in time. Accepts a Firestore `Timestamp`, a `Date`, an ISO string or
 * epoch milliseconds — the same logical field arrives in different shapes
 * depending on whether it came from a listener, a cached read, a Cloud Function
 * or the seeder.
 */
export const timestampSchema: Decoder<Date | null> = z
  .unknown()
  .transform((value): Date | null => {
    if (isTimestampLike(value)) return value.toDate();
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  });

/** A timestamp that must exist; falls back to the epoch so a decode never fails
 *  on a document written before the field was added. */
export const requiredTimestamp = (): Decoder<Date> =>
  timestampSchema.transform((value) => value ?? new Date(0));

export const nullableTimestamp = (): Decoder<Date | null> => timestampSchema;

/** A calendar date as `YYYY-MM-DD`. Null when absent or unparseable. */
export const calendarDateSchema: Decoder<CalendarDate | null> = z
  .unknown()
  .transform((value): CalendarDate | null => {
    if (typeof value !== 'string') return null;
    const parsed = CalendarDate.fromISO(value);
    return parsed.ok ? parsed.value : null;
  });

/**
 * A stored range. Returns null when either end is missing or the pair is
 * invalid, which the entity decoders treat as a reason to skip that one document
 * — a request with no dates is not renderable, and guessing would be worse.
 */
export const dateRangeFrom = (
  startISO: unknown,
  endISO: unknown,
): DateRange | null => {
  if (typeof startISO !== 'string' || typeof endISO !== 'string') return null;
  const range = DateRange.fromISO(startISO, endISO);
  return range.ok ? range.value : null;
};

/** A branded id. The single legitimate place to mint one from a raw string. */
export const brandedId = <B extends string>(): Decoder<Brand<string, B>> =>
  z.string().min(1).transform((raw) => asId<B>(raw));

/** A string with a default, so an absent field never reads as `undefined`. */
export const textOr = (fallback: string): Decoder<string> =>
  z.unknown().transform((value) => (typeof value === 'string' ? value : fallback));

export const trimmedTextOr = (fallback: string): Decoder<string> =>
  z
    .unknown()
    .transform((value) => (typeof value === 'string' ? value.trim() : fallback));

/** A nullable string: absent, null and non-string all collapse to null. */
export const nullableText = (): Decoder<string | null> =>
  z.unknown().transform((value) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  });

export const numberOr = (fallback: number): Decoder<number> =>
  z.unknown().transform((value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    // Firestore occasionally yields a numeric string from a hand-edited document
    // or a CSV import; accepting it is strictly better than showing a zero.
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return fallback;
  });

export const integerOr = (fallback: number): Decoder<number> =>
  numberOr(fallback).transform((value) => Math.trunc(value));

export const booleanOr = (fallback: boolean): Decoder<boolean> =>
  z.unknown().transform((value) => (typeof value === 'boolean' ? value : fallback));

/**
 * A closed vocabulary that degrades instead of failing.
 *
 * This is point 3 above, and the single most important line for OTA safety. When
 * a newer deploy introduces a status, an older client renders it as `Desconocido`
 * and the rest of the row still works.
 */
export const enumOr = <T extends string>(
  allowed: readonly T[],
  fallback: T,
): Decoder<T> =>
  z
    .unknown()
    .transform((value) =>
      typeof value === 'string' && (allowed as readonly string[]).includes(value)
        ? (value as T)
        : fallback,
    );

/** A vocabulary whose fallback is the shared `Desconocido` sentinel. */
export const vocabularyOr = <T extends string>(
  allowed: readonly T[],
): Decoder<T | typeof UNKNOWN> =>
  z
    .unknown()
    .transform((value) =>
      typeof value === 'string' && (allowed as readonly string[]).includes(value)
        ? (value as T)
        : UNKNOWN,
    );

/** An array that tolerates a non-array value by yielding the empty list. */
export const arrayOr = <T>(item: Decoder<T>): Decoder<T[]> =>
  z.unknown().transform((value) => {
    if (!Array.isArray(value)) return [];
    // Elements are filtered individually: one malformed entry in a catalogue must
    // not discard the whole catalogue.
    const out: T[] = [];
    for (const element of value) {
      const parsed = item.safeParse(element);
      if (parsed.success) out.push(parsed.data);
    }
    return out;
  });

/** The version stamp every document carries. */
export const CURRENT_SCHEMA_VERSION = 1;

export const schemaVersionSchema: Decoder<number> = integerOr(CURRENT_SCHEMA_VERSION);

/** The audit fields shared by every persisted record. */
export const auditableShape = {
  createdAt: requiredTimestamp(),
  updatedAt: requiredTimestamp(),
  schemaVersion: schemaVersionSchema,
};

// ---------------------------------------------------------------------------
// Decoding
// ---------------------------------------------------------------------------

export interface DecodeFailure {
  readonly path: string;
  readonly detail: string;
}

export interface DecodeManyResult<T> {
  readonly items: readonly T[];
  /** Documents that could not be decoded. Logged, never thrown. */
  readonly failures: readonly DecodeFailure[];
}

/**
 * Decodes a page of documents, skipping the ones that cannot be read.
 *
 * The deliberate choice here is partial success. A single corrupt document — a
 * request whose dates were wiped by a bad migration, say — must not blank the
 * whole list. The failures come back so the caller can log them and, if it wants,
 * tell the user "1 registro no se pudo leer" while still showing the other 499.
 */
export const decodeMany = <Raw, T>(
  documents: readonly { readonly id: string; readonly data: Raw }[],
  decode: (id: string, data: Raw) => T | null,
  collectionPath: string,
): DecodeManyResult<T> => {
  const items: T[] = [];
  const failures: DecodeFailure[] = [];

  for (const document of documents) {
    try {
      const decoded = decode(document.id, document.data);
      if (decoded === null) {
        failures.push({
          path: `${collectionPath}/${document.id}`,
          detail: 'required field missing or invalid',
        });
        continue;
      }
      items.push(decoded);
    } catch (cause) {
      failures.push({
        path: `${collectionPath}/${document.id}`,
        detail: cause instanceof Error ? cause.message : String(cause),
      });
    }
  }

  return { items, failures };
};

/**
 * Parses with a schema, returning null instead of throwing.
 *
 * Every schema in this project is built so that parsing only fails when a field
 * the entity genuinely cannot exist without is unusable. Everything softer has a
 * fallback baked in, so a null here means "skip this document", not "the app is
 * broken".
 */
export const safeDecode = <S extends z.ZodTypeAny>(
  schema: S,
  data: unknown,
): z.output<S> | null => {
  // `safeParse` is typed loosely on a generic schema, so the success branch is
  // narrowed explicitly rather than returned straight through.
  const parsed: z.SafeParseReturnType<z.input<S>, z.output<S>> = schema.safeParse(data);
  if (!parsed.success) return null;
  return parsed.data;
};
